import { motion } from "framer-motion";
import { Bot, Database, Compass, Workflow, ArrowUpRight } from "lucide-react";
import { Reveal, MaskLines, Overline, EASE } from "./Reveal";

const Card = ({ testId, className, children, delay }) => (
  <motion.article
    data-testid={testId}
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, ease: EASE, delay }}
    whileHover={{ y: -6 }}
    className={`group relative overflow-hidden rounded-[28px] border border-ink/10 ${className}`}
  >
    {children}
  </motion.article>
);

const Tags = ({ items, dark }) => (
  <div className="mt-6 flex flex-wrap gap-2">
    {items.map((t) => (
      <span key={t} className={`rounded-full border px-3 py-1 font-mono text-xs ${dark ? "border-white/25 text-white/90" : "border-ink/15 text-ink/80"}`}>{t}</span>
    ))}
  </div>
);

export const Services = () => (
  <section id="services" data-testid="services-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-6 md:py-36 lg:px-8">
    <div className="mb-16 grid grid-cols-1 gap-8 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <Overline>01 — Services</Overline>
        <h2 className="font-display text-5xl leading-[1] tracking-tight text-ink sm:text-6xl lg:text-7xl">
          <MaskLines inView lines={[<>Four practices.</>, <em className="text-violet">One intelligent stack.</em>]} />
        </h2>
      </div>
      <Reveal className="self-end lg:col-span-5" delay={0.2}>
        <p className="text-base leading-relaxed text-ink/75 md:text-lg">From the first strategy workshop to models serving real users, our teams cover the full path — strategy, data, models and the software around them.</p>
      </Reveal>
    </div>

    <div className="grid grid-cols-1 gap-5 md:grid-cols-12 md:gap-6">
      <Card testId="service-card-genai" className="bg-ink text-white md:col-span-7 md:row-span-2" delay={0}>
        <img src="/img/discs.jpg" alt="Glass forms" className="absolute inset-0 h-full w-full object-cover opacity-60 transition-transform duration-[1.4s] group-hover:scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-transparent" />
        <div className="relative flex h-full min-h-[460px] flex-col justify-end p-7 sm:p-10">
          <Bot className="mb-auto h-10 w-10 text-amber" />
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/80">Practice 01</p>
          <h3 className="mt-3 font-display text-4xl leading-tight sm:text-5xl">Generative AI & LLM apps</h3>
          <p className="mt-4 max-w-md text-base leading-relaxed text-white/85">Copilots, RAG knowledge assistants, document intelligence and agentic workflows — grounded in your data, evaluated and guarded for production.</p>
          <Tags dark items={["RAG", "Agents", "Fine-tuning", "Evals"]} />
        </div>
      </Card>

      <Card testId="service-card-data" className="bg-emerald/15 md:col-span-5" delay={0.1}>
        <div className="p-7 sm:p-9">
          <div className="flex items-start justify-between">
            <Database className="h-9 w-9 text-ink" />
            <ArrowUpRight className="h-6 w-6 text-ink transition-transform duration-500 group-hover:rotate-45" />
          </div>
          <h3 className="mt-10 font-display text-3xl leading-tight text-ink sm:text-4xl">Data engineering & analytics</h3>
          <p className="mt-3 text-base leading-relaxed text-ink/75">Pipelines, lakehouses and dashboards that make your data AI-ready — and decisions faster.</p>
          <Tags items={["ETL/ELT", "Lakehouse", "BI"]} />
        </div>
      </Card>

      <Card testId="service-card-consulting" className="bg-violet text-white md:col-span-5" delay={0.2}>
        <div className="p-7 sm:p-9">
          <div className="flex items-start justify-between">
            <Compass className="h-9 w-9 text-amber" />
            <ArrowUpRight className="h-6 w-6 transition-transform duration-500 group-hover:rotate-45" />
          </div>
          <h3 className="mt-10 font-display text-3xl leading-tight sm:text-4xl">AI consulting & custom ML</h3>
          <p className="mt-3 text-base leading-relaxed text-white/85">Roadmaps, feasibility, and bespoke models for forecasting, vision, NLP and recommendation.</p>
          <Tags dark items={["Strategy", "MLOps", "Forecasting"]} />
        </div>
      </Card>

      <Card testId="service-card-automation" className="bg-white md:col-span-12" delay={0.1}>
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="p-7 sm:p-10">
            <Workflow className="h-9 w-9 text-coral" />
            <h3 className="mt-8 font-display text-3xl leading-tight text-ink sm:text-4xl">Chatbots & intelligent automation</h3>
            <p className="mt-3 max-w-md text-base leading-relaxed text-ink/75">Conversational assistants for customers and teams, plus automations that remove repetitive work across your tools.</p>
            <Tags items={["Chatbots", "Voice", "Integrations", "Workflow"]} />
          </div>
          <div className="relative min-h-[260px] overflow-hidden md:m-3 md:rounded-[22px]">
            <img src="/img/cables.jpg" alt="Network cabling" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.4s] group-hover:scale-110" />
          </div>
        </div>
      </Card>
    </div>
  </section>
);
